import { Modal, ActionButton, DataTable, Pill, PopOverMenu } from 'components/commons';
import { useApi, useFilter, useModal } from 'hooks';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styles from './customer-account-claims-modal.module.scss';
import { getCustomerClaimsByCustomerAccount } from 'apis/customer.api';
import CustomerStatus from 'enums/driver-status';
import { formatCurrency, formatDate, prettifyCustomerStatus } from 'utils';
import { customerClaimsColumns } from './customer-account-claims-modal-columns';
import { customerAccountClaimsFilterState } from './customer-account-claims-modal-filter.state';
import CustomerAccountClaimsConfirmationModal from './customer-account-claims-confirmation-modal';
import { DateTime } from 'enums';
import { UserContext } from 'contexts';
import { prettifyRequestAction } from 'utils/pretty.utils';

const CustomerAccountClaimsModal = ({ customerAccountId, close, active }) => {
  const user = useContext(UserContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const statusModal = useModal();
  // const { request: updateCustomerAccountRequest, loading: updatingCustomerAccount } = useApi({
  //   api: updateCustomerAccount
  // });

  // const updateStatusCb = useCallback(async () => {
  //   handleRequest(async () => {
  //     await updateCustomerAccountRequest({ customerAccountId, productId }, () => updateStatusCb());
  //     refetchCustomers();
  //     close();
  //   });
  // }, [updateCustomerAccountRequest, customerAccountId, productId, refetchCustomers, close]);

  const { modifyFilters, requestState } = useFilter(customerAccountClaimsFilterState());

  const {
    request: getCustomerClaimRequest,
    loading: fetchingCustomerClaim,
    result: getCustomerClaimResult = { customerClaims: [], count: 0 }
  } = useApi({
    api: getCustomerClaimsByCustomerAccount,
    pageError: true,
    params: {
      customerAccountId
    }
  });

  const changeStatusCb = useCallback(
    (interestAmount, customerClaimId, status, requestAction) => {
      statusModal.show({ interestAmount, customerClaimId, status, requestAction });
    },
    [statusModal]
  );

  const preparedCustomerClaimsData = useMemo(() => {
    const { customerClaims } = getCustomerClaimResult;
    if (customerClaims.length > 0) {
      const preparedData = customerClaims.map((c) => {
        const map = new Map();
        map.set('periodYear', c?.periodYear || '--');
        map.set('principalAmount', formatCurrency(c?.principalAmount || 0));
        map.set('interestAmount', formatCurrency(c?.interestAmount || 0));
        map.set('interest', c?.interest || 0);
        map.set(
          'status',
          <>
            <Pill
              grass={c.status === CustomerStatus.Claimed}
              sky={c.status === CustomerStatus.Pending}
              error={c.status === CustomerStatus.Rejected}
            >
              {prettifyCustomerStatus(c.status)}
            </Pill>
            <div className={styles.subBusinessId}>
              {c.status === CustomerStatus.Claimed && formatDate(c.claimedAt, DateTime.T)}
              {c.status === CustomerStatus.Rejected && c.rejectedAt && formatDate(c.rejectedAt, DateTime.T)}
              {c.status === CustomerStatus.Pending && c.requestedAt && formatDate(c.requestedAt, DateTime.T)}
            </div>
          </>
        );
        map.set(
          'approvalStatus',
          <>
            <b>{prettifyRequestAction(c.requestAction)}</b>
            <div className={styles.subBusinessId}>
              {c.status === CustomerStatus.Rejected && c.rejectedAt && formatDate(c.rejectedAt, DateTime.T)}
              {c.status === CustomerStatus.Pending && c.requestedAt && formatDate(c.requestedAt, DateTime.T)}
            </div>
          </>
        );
        const options = [];

        if (c.status === CustomerStatus.Pending) {
          if (['super-admin', 'maker'].includes(user?.role) && !c.requestAction) {
            options.push({
              content: 'Transfer to Savings',
              onClick: () => {
                changeStatusCb(c.interestAmount, c.customerClaimId, c.status, 'transfer-savings');
              }
            });
          } else if (['admin', 'approver'].includes(user?.role)) {
            options.push({
              content: 'Approve',
              onClick: () => {
                changeStatusCb(c.interestAmount, c.customerClaimId, 'approve', c.requestAction);
              }
            });
            options.push({
              content: 'Reject',
              onClick: () => {
                changeStatusCb(c.interestAmount, c.customerClaimId, 'reject', c.requestAction);
              }
            });
          }
        }

        if (options && options.length > 0) {
          map.set('options', <PopOverMenu options={options} />);
        }

        return map;
      });
      return preparedData;
    }
    return [];
  }, [changeStatusCb, getCustomerClaimResult, user?.role]);

  const fetchCustomerClaims = useCallback(
    (rs) => {
      getCustomerClaimRequest();
    },
    [getCustomerClaimRequest]
  );

  useEffect(() => {
    if (active && !isLoaded) {
      fetchCustomerClaims(requestState);
      setIsLoaded(true);
    }
  }, [active, fetchCustomerClaims, getCustomerClaimRequest, isLoaded, requestState]);

  useEffect(() => {
    if (close && !active) {
      setIsLoaded(false);
    }
  }, [active, close, fetchCustomerClaims]);

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchCustomerClaims(requestState);
    },
    [fetchCustomerClaims, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchCustomerClaims(requestState);
    },
    [fetchCustomerClaims, modifyFilters]
  );

  return (
    <>
      <CustomerAccountClaimsConfirmationModal refetchCustomerClaims={() => fetchCustomerClaims(requestState)} {...statusModal} />
      <Modal active={active} title={'Claims History'} close={close} className={styles.modal}>
        <div className={styles.content}>
          <DataTable
            loading={fetchingCustomerClaim}
            columns={customerClaimsColumns}
            data={preparedCustomerClaimsData}
            dataCount={getCustomerClaimResult.count}
            onChangePage={onChangePageCb}
            onChangePageSize={onChangePageSizeCb}
            isPaginationHidden={true}
          />
        </div>
        <ActionButton
          right
          items={[
            {
              text: 'Close',
              onClick: close
            }
          ]}
        />
      </Modal>
    </>
  );
};

export default CustomerAccountClaimsModal;
